import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import videojs from 'video.js';
import 'videojs-landscape-fullscreen';
import 'jb-videojs-hls-quality-selector';
import 'videojs-mobile-ui';
import 'videojs-mobile-ui/dist/videojs-mobile-ui.css';
import overlay from 'videojs-overlay';
import {
    SET_CONTROL_BAR,
    SET_VIDEO_MUTED_STATE,
    SET_WATCH_COUNT
} from '../../constants/actionTypes';
import styles from './player.module.scss';
import './videojs-airplay.scss';
import './videojs-custom-seeker.css';
import './videojs-overlay.scss';
import { isMobile } from 'react-device-detect';
import '@videojs/themes/dist/forest/index.css';
import clsx from 'clsx';

const mapStateToProps = (state) => {
    return {
        user: state.user.currentUser,
        _user: state.user.currentUser,
        videoState: state.appState.video,
        company: state.common.company,
        controlBar: state.common.company
    };
};

const mapDispatchToProps = (dispatch) => ({
    setVideoMuted: (payload) => dispatch({ type: SET_VIDEO_MUTED_STATE, payload }),
    setControlBar: (payload) => dispatch({ type: SET_CONTROL_BAR, payload }),
    setWatchCount: (payload) => dispatch({ type: SET_WATCH_COUNT, payload })
});

const Player = (props) => {
    // const [logo, setLogo] = useState(false);
    const [isPortrait, setIsPortrait] = useState(false);
    const playerRef = useRef();
    const videoRef = useRef(null);
    const [user, setUser] = useState(props.user);
    const [errorFlag, setErrorFlag] = useState(true);
    const [showThumbnail, setShowThumbnail] = useState(true);
    const [showButton, setShowButon] = useState(true);

    // useEffect(() => {
    //     setLogo(
    //         props.company &&
    //             props.company.company_logo_text &&
    //             sanity.urlFor(props.company.company_logo_text.asset._ref)
    //     );
    // }, [props]);

    useEffect(() => {
        if (props.user.data) {
            setUser(props.user);
        }
    }, [props.user]);

    let _videoPausedFlag = false;
    let watchIntervalRef = useRef();
    let tenPercentWatched = useRef(false);

    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent =
            '.player-dimensions.vjs-fluid:not(.vjs-audio-only-mode) { padding-top: 56.25% !important; }';

        document.head.appendChild(styleElement);

        return () => {
            // Cleanup: Remove the style element when the component is unmounted
            document.head.removeChild(styleElement);
        };
    }, [isPortrait]);

    useEffect(() => {
        const videoJsOptions = {
            preload: 'auto',
            controls: true,
            playsinline: true,
            sources:
                props.event.trailer && props.showTrailer
                    ? [
                          {
                              src: props.event.trailerUrl,
                              type: 'application/x-mpegURL'
                          }
                      ]
                    : [
                          {
                              src: props.streamSource,
                              type: 'application/x-mpegURL'
                          }
                      ],
            techOrder: ['html5', 'HLS'],
            errorDisplay: false,
            html5: {
                vhs: {
                    enableLowInitialPlaylist: false,
                    smoothQualityChange: true,
                    useBandwidthFromLocalStorage: true
                }
            }
        };

        if (!playerRef.current) {
            playerRef.current = videojs(videoRef.current, videoJsOptions, props.onPlayerReady);
            playerRef.current.hlsQualitySelector({
                displayCurrentQuality: true
            });
        } else {
            playerRef.current?.src(videoJsOptions.sources);
        }
    }, [
        props.event.trailer,
        props.event.trailerUrl,
        props.onPlayerReady,
        props.showTrailer,
        props.streamSource
    ]);

    useEffect(() => {
        const player = playerRef.current;

        if (player) {
            player.ready(() => {
                player.pause();
                player.volume(0.7);
            });
        }

        return () => {
            if (player && !player.isDisposed()) {
                player.dispose();
                playerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const player = playerRef.current;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                player.muted(true);
            } else {
                player.muted(false);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        if (player) {
            const loadedPlugins = videojs.getPlugins();
            if (!loadedPlugins.overlay) videojs.registerPlugin('overlay', overlay);

            player.on('loadedmetadata', () => {
                const videoWidth = player.videoWidth();
                const videoHeight = player.videoHeight();
                setIsPortrait(videoWidth < videoHeight);
            });

            player.on('pause', () => {
                _videoPausedFlag = true;
                clearInterval(player._updateInterval);
                player.trigger('userInActive');
            });

            player.on('play', () => {
                setShowThumbnail(false);
                setShowButon(false);
                _videoPausedFlag = false;
                watchIntervalRef = setInterval(() => {
                    try {
                        const currentTime = player.currentTime();
                        const duration = player.duration();
                        if (!tenPercentWatched.current && currentTime >= 0.1 * duration) {
                            tenPercentWatched.current = true; // Set the flag to true to avoid multiple triggers
                            props.setWatchCount({
                                user: props.user.data.sub,
                                eventId: props.event.id,
                                companyId: props.company._id
                            });
                            clearInterval(watchIntervalRef.current);
                        }
                    } catch (e) {
                        //ignore this error
                    }
                }, 3000);
                player.trigger('userInActive');
            });

            player.on('error', (event) => {
                if (errorFlag) {
                    props.setTechnicalError?.(true);
                    setErrorFlag(false);
                }
            });

            player.on('useractive', () => _videoPausedFlag != true && player.trigger('userActive'));

            player.on('userinactive', () => player.trigger('userInActive'));
        }

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (watchIntervalRef) {
                clearInterval(watchIntervalRef.current);
            }
        };
    }, [props.displaySeconds, user]);

    return (
        <>
            <div
                style={
                    props.style
                        ? { position: 'relative', ...props.style }
                        : { position: 'relative' }
                }
                className={`data-vjs-player video-container `}>
                {showThumbnail && props?.thumbnail && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundImage: `url(${props.thumbnail})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            zIndex: 1,
                            backgroundColor: '#252525',
                            pointerEvents: 'none',
                            borderRadius: '12px'
                        }}
                        onClick={() => playerRef.current && playerRef.current.play()}></div>
                )}
                {showButton && (
                    <div
                        className="custom-play-button"
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 2, // Ensure it's above the thumbnail
                            cursor: 'pointer'
                        }}
                        onClick={() => playerRef.current && playerRef.current.play()}>
                        <div
                            className="vjs-big-play-button"
                            style={{
                                width: isMobile ? '50px' : '70px',
                                height: isMobile ? '50px' : '70px',
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2
                            }}>
                            <i
                                className="vjs-icon-play"
                                style={{
                                    fontSize: '32px',
                                    color: 'white'
                                }}
                            />
                        </div>
                    </div>
                )}
                <video
                    id="player"
                    preload="metadata"
                    ref={videoRef}
                    className={clsx([
                        'video-js',
                        'vjs-big-play-centered',
                        'vjs-theme-forest',
                        styles.player
                    ])}
                    data-setup='{ "fluid" : true }'
                    playsInline
                    style={props.style ?? {}}
                />
            </div>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Player);
